import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  HeartHandshake,
  Sparkles,
  Users,
  Compass,
  MessageSquare,
  Sprout,
  GraduationCap,
} from "lucide-react";
import SEO from "../components/SEO";

const VALUES = [
  { icon: Sparkles, t: "Confidence" },
  { icon: Sprout, t: "Emotional growth" },
  { icon: Users, t: "Inclusion" },
  { icon: Compass, t: "Practical skill-building" },
  { icon: MessageSquare, t: "Family communication" },
  { icon: HeartHandshake, t: "Child-centred support" },
  { icon: GraduationCap, t: "Learning through play" },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        path="/about"
        title="About Nexora Learning & Development Centre"
        description="Nexora Learning & Development Centre supports children through structured, engaging, development-focused learning. Current focus: Play-Based Behaviour & Emotional Support."
      />

      <section className="relative overflow-hidden bg-[#FFFCF7]">
        <div className="absolute -top-20 right-0 w-[34rem] h-[34rem] rounded-full bg-sky-100/60 blur-[120px] opacity-70 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-24 pb-20 relative">
          <p className="eyebrow">About</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tight font-extrabold text-slate-900 mt-4 max-w-4xl leading-[1.04]">
            A children's learning and development brand, built for the long term.
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Nexora Learning & Development Centre was created to support children
            through structured, engaging, and development-focused learning
            experiences.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <p className="eyebrow">Current focus</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold text-slate-900 mt-3 leading-[1.05]">
              Play-Based Behaviour & Emotional Support.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Our current focus is Play-Based Behaviour & Emotional Support —
              helping children build confidence, emotional regulation, focus,
              social skills, and positive learning behaviours through guided
              play and supportive 1-on-1 sessions.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Nexora is being built as a long-term children's learning and
              development brand. Future programs may be added under the same
              brand, but the current launch focus is Play-Based Behaviour &
              Emotional Support.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/play-based-support"
                className="btn-primary"
                data-testid="about-program-cta"
              >
                Learn about the program
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="btn-soft"
                data-testid="about-contact-cta"
              >
                Contact us
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-[2rem] bg-white border border-slate-200/70 p-7 shadow-sm">
              <p className="eyebrow">Our values</p>
              <ul className="mt-5 grid gap-2">
                {VALUES.map((v, i) => (
                  <motion.li
                    key={v.t}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.04 }}
                    className="flex items-center gap-3 rounded-2xl bg-[#FFFCF7] border border-slate-200/60 px-4 py-3"
                    data-testid={`about-value-${i}`}
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-sky-50 text-[#0369A1]">
                      <v.icon className="h-4 w-4" />
                    </span>
                    <span className="font-display font-bold text-slate-900 text-sm">
                      {v.t}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-[2.5rem] bg-slate-900 text-white p-10 sm:p-14 grid md:grid-cols-2 gap-8 items-center">
          <h3 className="font-display text-3xl sm:text-4xl tracking-tight font-extrabold leading-[1.05]">
            Curious whether Nexora is right for your child?
          </h3>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-900 px-7 py-3.5 text-sm font-bold"
              data-testid="about-final-cta"
            >
              Request Information
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
